import React, { useEffect } from 'react';
import { X, Radio } from 'lucide-react';
import { Channel } from '../types';
import { programs } from '../data/channels';

interface ChannelOverlayProps {
  show: boolean;
  onClose: () => void;
  channels: Channel[];
  selectedChannel: Channel | null;
  onSelectChannel: (channel: Channel) => void;
}

export function ChannelOverlay({
  show,
  onClose,
  channels,
  selectedChannel,
  onSelectChannel,
}: ChannelOverlayProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const getCurrentProgram = (channelId: string) => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const currentTime = `${hours}:${minutes}`;
    
    return programs.find(program => 
      program.channelId === channelId &&
      program.startTime <= currentTime &&
      program.endTime >= currentTime
    );
  };

  return (
    <div className={`channel-overlay ${show ? 'active' : ''}`}>
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 flex items-center justify-between bg-gradient-to-b from-black to-transparent">
          <div className="flex items-center gap-3">
            <Radio className="h-5 w-5 text-red-500" />
            <h2 className="text-2xl font-bold">Canais ao Vivo</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Channel Grid */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-w-7xl mx-auto">
            {channels.map((channel) => {
              const currentProgram = getCurrentProgram(channel.id);
              const isSelected = selectedChannel?.id === channel.id;

              return (
                <button
                  key={channel.id}
                  onClick={() => onSelectChannel(channel)}
                  className={`channel-card group ${
                    isSelected ? 'ring-2 ring-red-500' : ''
                  }`}
                >
                  <div className="aspect-video relative overflow-hidden rounded-t-lg">
                    <img 
                      src={channel.thumbnail} 
                      alt={channel.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-3 left-3 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-black/50 backdrop-blur-sm flex items-center justify-center">
                        <span className="text-lg">{channel.logo}</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 bg-black/50 backdrop-blur-sm rounded-b-lg">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium truncate">{channel.name}</h3>
                      {isSelected && (
                        <span className="text-xs text-red-500 live-dot pl-3">
                          AO VIVO
                        </span>
                      )}
                    </div>
                    {currentProgram && (
                      <p className="text-sm text-gray-400 truncate">
                        {currentProgram.title}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}